import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AccountSidebarComponent } from '@app/+authenticated/+account/sidebar/sidebar.component';
import { MyChecklistWidgetComponent } from '@app/+authenticated/dashboard/my-dashboard/dashboard-widgets/my-checklist-widget/my-checklist-widget.component';
import { AppSidePanelComponent } from '@app/+authenticated/shared/app-side-panel/app-side-panel.component';
import { SidePanelHeaderComponent } from '@app/+authenticated/shared/app-side-panel/side-panel-header.component';
import { KnowledgeBaseArticleLinkModule } from '@app/pipes/knowledge-base-article-link.module';
import { SubscriptionPlanGuard } from '@app/shared/subscription-plan/subscription-plan.guard';
import { VerificationBannerComponent } from '@app/shared/verification-banner/verification-banner.component';
import { FeatureFlagPipe } from '@sb/shared/feature';
import { KnowledgeBaseArticleLinkPipe } from '@sb/shared/knowledge-base';
import { BadgeComponent } from '@sb/ui/badge';
import { CheckboxComponent } from '@sb/ui/checkbox';
import { EmptyStateComponent } from '@sb/ui/feedback';
import { IconComponent } from '@sb/ui/icon';
import { SidebarDrawerModule } from '@sb/ui/navigation';

import { UniversalLinkComponent } from '../+anonymous/universal/universal-link.component';
import { AuthenticatedGuard } from '../startup/authenticated-guard.service';
import { payrollRoutes } from './+payroll/payroll.routes';
import { ScheduleSidebarService } from './+schedule/employee/schedule-employee-n-period/schedule-sidebar.service';
import { authenticatedAccess } from './authenticated.access';
import { AuthenticatedComponent } from './authenticated.component';
import { CommandPaletteComponent } from './command-palette/command-palette.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { MyAbsenceComponent } from './dashboard/my-absence/my-absence.component';
import { MyDashboardSidebarComponent } from './dashboard/my-dashboard/my-dashboard-sidebar/my-dashboard-sidebar.component';
import { MyDashboardComponent } from './dashboard/my-dashboard/my-dashboard.component';
import { MyDashboardModule } from './dashboard/my-dashboard/my-dashboard.module';
import { MyHoursComponent } from './dashboard/my-hours/my-hours.component';
import { MyPlusMinusComponent } from './dashboard/my-plus-minus/my-plus-minus.component';
import { MyScheduleComponent } from './dashboard/my-schedule/my-schedule.component';
import { insightsRoutes } from './insights/insights-routes';
import { LogoutComponent } from './logout.component';
import { PermissionGuard } from './permission-guard.service';
import { AppSideNavComponent } from './shared/app-side-nav/app-side-nav.component';
import { FirstTouchComponent } from './shared/first-touch/first-touch.component';
import { FirstTouchService } from './shared/first-touch/first-touch.service';
import { LocaleService } from './shared/locale/locale.service';
import { SharedAuthModule } from './shared/shared-auth.module';
import { OpenShiftsComponent } from './shared/sidebar/my-requests-sidebar/open-shifts.component';
import { DashboardMenuRoutes } from './shared/sub-menu/dashboard-menu.routes';

const routes: Routes = [
  {
    path: '',
    component: AuthenticatedComponent,
    canActivate: [AuthenticatedGuard],
    canActivateChild: [PermissionGuard],
    children: [
      ...DashboardMenuRoutes,
      {
        path: 'communications',
        loadChildren: () => import('./+communications/communications.routes').then((m) => m.communicationsRoutes),
      },
      {
        path: 'news',
        redirectTo: 'communications/news',
      },
      {
        path: 'files',
        redirectTo: 'communications/files',
      },
      {
        path: 'my-account',
        loadChildren: () => import('./+my-account/my-account.module').then((m) => m.MyAccountModule),
      },
      {
        path: 'account',
        loadChildren: () => import('./+account/account.module').then((m) => m.AccountModule),
      },
      {
        path: 'employees',
        loadChildren: () => import('./+employees/employees.module').then((m) => m.EmployeesModule),
      },
      {
        path: 'hiring',
        loadChildren: () => import('./+hiring/hiring.module').then((m) => m.HiringModule),
        data: { ...authenticatedAccess.hiring },
        canActivate: [SubscriptionPlanGuard],
      },
      {
        path: 'offers',
        loadChildren: () => import('./+offers/offers.routes').then((m) => m.offersRoutes),
      },
      {
        path: 'schedule',
        loadChildren: () => import('./+schedule/schedule.module').then((m) => m.ScheduleModule),
      },
      {
        path: 'timesheet',
        loadChildren: () => import('./+timesheet/timesheet.module').then((m) => m.TimesheetModule),
        data: { ...authenticatedAccess.timesheet },
        canActivate: [PermissionGuard],
      },
      {
        path: 'diary',
        redirectTo: 'insights',
        pathMatch: 'full',
      },
      ...insightsRoutes,
      ...payrollRoutes,
      {
        path: 'reports',
        data: { ...authenticatedAccess.reports },
        canActivate: [PermissionGuard],
        loadChildren: () => import('./+reports/reports.module').then((m) => m.ReportsModule),
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard/my-overview',
      },
      { path: 'universal/:type', component: UniversalLinkComponent },
      { path: 'universal/:type/:id', component: UniversalLinkComponent },
    ],
  },
  {
    path: 'logout',
    component: LogoutComponent,
    canActivate: [AuthenticatedGuard],
  },
  {
    path: 'mobile-welcome',
    loadComponent: () => import('./mobile-welcome/mobile-welcome.component').then((m) => m.MobileWelcomeComponent),
  },
];

@NgModule({
  imports: [
    SharedAuthModule,
    MyDashboardModule,
    RouterModule.forChild(routes),
    AppSideNavComponent,
    OpenShiftsComponent,
    KnowledgeBaseArticleLinkModule,
    VerificationBannerComponent,
    FeatureFlagPipe,
    IconComponent,
    AccountSidebarComponent,
    SidebarDrawerModule,
    MyChecklistWidgetComponent,
    EmptyStateComponent,
    FirstTouchComponent,
    CheckboxComponent,
    BadgeComponent,
    AppSidePanelComponent,
    SidePanelHeaderComponent,
    CommandPaletteComponent,
  ],
  providers: [LocaleService, ScheduleSidebarService, KnowledgeBaseArticleLinkPipe, FirstTouchService],
  declarations: [
    AuthenticatedComponent,
    LogoutComponent,
    DashboardComponent,
    MyDashboardSidebarComponent,
    MyDashboardComponent,
    MyHoursComponent,
    MyAbsenceComponent,
    MyPlusMinusComponent,
    MyScheduleComponent,
    UniversalLinkComponent,
  ],
})
export class AuthenticatedModule {}
